import { BASEMAP_STYLES_CONFIG } from '../config';
import { store, AppState } from '../state';
import TierAccessService, {
  AccessTier,
  TIER_RANK,
  getRequiredTierForBasemapTheme,
  getRequiredTierForLayer,
} from '../services/TierAccessService';

export class BaseMapPopover {
  private container!: HTMLElement;
  private popoverCard!: HTMLElement;
  private controlsContainer!: HTMLElement;
  private isOpen = false;

  constructor(
    private parentElement: HTMLElement,
    private onBasemapSelect: (key: string) => void
  ) {
    this.render();
    this.setupStoreSubscription();
  }

  private isHostedVectorTheme(theme: string): boolean {
    return theme === 'road' || theme === 'terrain-mapterhorn' || theme === 'terrain' || theme === 'light' || theme === 'dark' || theme === 'neutral' || !theme;
  }

  private renderTierPillHtml(reqTier: AccessTier, floatTopRight = false): string {
    const label = TierAccessService.getTierBadgeLabel(reqTier);
    const isPaid = TIER_RANK[reqTier] >= TIER_RANK.pro;
    const border = isPaid ? 'rgba(251, 191, 36, 0.7)' : 'rgba(56, 189, 248, 0.7)';
    const bg = isPaid ? 'rgba(120, 53, 15, 0.92)' : 'rgba(12, 74, 110, 0.92)';
    const fg = isPaid ? '#fde68a' : '#e0f2fe';
    if (floatTopRight) {
      return `<span class="gw-basemap-tier-pill" aria-label="Requires ${label}" style="position: absolute; top: 4px; right: 4px; font-size: 0.54rem; font-weight: 800; line-height: 1.1; padding: 2px 4px; border-radius: 4px; background: ${bg}; color: ${fg}; border: 1px solid ${border}; letter-spacing: 0.3px;">🔒 ${label}</span>`;
    }
    return `<span class="gw-basemap-tier-pill" aria-label="Requires ${label}" style="display: inline-flex; align-items: center; gap: 2px; margin-left: 4px; font-size: 0.56rem; font-weight: 800; line-height: 1.1; padding: 1px 4px; border-radius: 3px; background: ${bg}; color: ${fg}; border: 1px solid ${border}; letter-spacing: 0.3px;">🔒 ${label}</span>`;
  }

  private updateThumbnailLockBadges(state: AppState = store.getState()) {
    if (!this.popoverCard) return;
    const cards = this.popoverCard.querySelectorAll<HTMLElement>('.gw-basemap-thumbnail-card');
    cards.forEach((card) => {
      const key = card.dataset.key || '';
      const imgEl = card.querySelector<HTMLElement>('.gw-thumb-img');
      if (!imgEl) return;
      const existing = imgEl.querySelector('.gw-basemap-tier-pill');
      if (existing) existing.remove();
      if (!TierAccessService.userHasBasemapAccess(key, 'off', state)) {
        const reqTier = getRequiredTierForBasemapTheme(key, 'off');
        imgEl.insertAdjacentHTML('beforeend', this.renderTierPillHtml(reqTier, true));
      }
    });
  }

  private render() {
    this.container = document.createElement('div');
    this.container.className = 'gw-basemap-switcher-container';

    // 3 Stacked Rectangles Icon Button
    const btn = document.createElement('button');
    btn.className = 'gw-basemap-btn';
    btn.setAttribute('aria-label', 'Select Base Map');
    btn.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
        <path d="M2 17l10 5 10-5"></path>
        <path d="M2 12l10 5 10-5"></path>
      </svg>
    `;

    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.togglePopover();
    });

    // Floating Popover Card
    this.popoverCard = document.createElement('div');
    this.popoverCard.className = 'gw-basemap-popover-card gw-hidden';

    const title = document.createElement('div');
    title.className = 'gw-popover-title';
    title.innerText = 'BASE MAPS';
    this.popoverCard.appendChild(title);

    const thumbnailsGrid = document.createElement('div');
    thumbnailsGrid.className = 'gw-basemap-thumbnails';

    const currentState = store.getState();
    const currentTheme = currentState.theme;

    BASEMAP_STYLES_CONFIG.forEach(cfg => {
      const card = document.createElement('div');
      card.className = 'gw-basemap-thumbnail-card';
      card.dataset.key = cfg.key;

      const isSelected = currentTheme === cfg.key ||
        (cfg.key === 'road' && (currentTheme === 'light' || currentTheme === 'dark' || currentTheme === 'terrain-mapterhorn' || currentTheme === 'terrain' || currentTheme === 'neutral' || !currentTheme)) ||
        (cfg.key === 'osm' && (currentTheme === 'openstreetmap' || currentTheme === 'osm'));
      if (isSelected) {
        card.classList.add('gw-selected');
      }

      const imgPlaceholder = document.createElement('div');
      imgPlaceholder.className = `gw-thumb-img gw-thumb-${cfg.key}`;
      imgPlaceholder.style.position = 'relative';
      imgPlaceholder.style.backgroundImage = `url(${cfg.thumbnailUrl})`;
      imgPlaceholder.style.backgroundSize = 'cover';
      imgPlaceholder.style.backgroundPosition = 'center';

      const label = document.createElement('span');
      label.className = 'gw-thumb-label';
      label.innerText = cfg.label;

      card.appendChild(imgPlaceholder);
      card.appendChild(label);

      card.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectBasemap(cfg.key);
      });

      thumbnailsGrid.appendChild(card);
    });

    this.popoverCard.appendChild(thumbnailsGrid);

    // Dynamic Controls Container (Mode Switcher + Layer Overlays)
    this.controlsContainer = document.createElement('div');
    this.controlsContainer.className = 'gw-basemap-controls-section';
    this.popoverCard.appendChild(this.controlsContainer);

    this.renderSubControls(currentState);
    this.updateThumbnailLockBadges(currentState);
    this.updateCardSelection(currentState.theme);

    this.container.appendChild(btn);
    this.container.appendChild(this.popoverCard);
    this.parentElement.appendChild(this.container);

    // Document click to close popover
    document.addEventListener('click', (e) => {
      if (this.isOpen && !this.container.contains(e.target as Node)) {
        this.closePopover();
      }
    });
  }

  private renderSubControls(state: AppState) {
    this.controlsContainer.innerHTML = '';
    const theme = state.theme;
    const isHosted = this.isHostedVectorTheme(theme);

    if (isHosted) {
      // 1. Dark / Light Mode Switcher
      const modeContainer = document.createElement('div');
      modeContainer.className = 'gw-basemap-mode-container';

      const modeTitle = document.createElement('div');
      modeTitle.className = 'gw-basemap-mode-title';
      modeTitle.innerText = 'APPEARANCE';
      modeContainer.appendChild(modeTitle);

      const toggleGroup = document.createElement('div');
      toggleGroup.className = 'gw-basemap-mode-toggle';

      const colorMode = state.vectorThemeMode || (theme === 'light' ? 'light' : 'dark');

      const lightBtn = document.createElement('button');
      lightBtn.className = `gw-basemap-mode-btn ${colorMode === 'light' ? 'active' : ''}`;
      lightBtn.innerHTML = `<span>☀️</span><span>Light</span>`;
      lightBtn.title = 'Switch to Light Cartography';
      lightBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ vectorThemeMode: 'light', savedBasemapOverrideState: null });
      });

      const darkBtn = document.createElement('button');
      darkBtn.className = `gw-basemap-mode-btn ${colorMode === 'dark' ? 'active' : ''}`;
      darkBtn.innerHTML = `<span>🌙</span><span>Dark</span>`;
      darkBtn.title = 'Switch to Dark Cartography';
      darkBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        store.update({ vectorThemeMode: 'dark', savedBasemapOverrideState: null });
      });

      toggleGroup.appendChild(lightBtn);
      toggleGroup.appendChild(darkBtn);
      modeContainer.appendChild(toggleGroup);
      this.controlsContainer.appendChild(modeContainer);

      // 2. Terrain Shading Sub-Toggle (Hypsometric Color vs Monochrome Hillshade)
      const isTerrainTheme = this.isHostedVectorTheme(theme);
      if (isTerrainTheme) {
        const reliefContainer = document.createElement('div');
        reliefContainer.className = 'gw-basemap-mode-container';
        reliefContainer.style.marginTop = '8px';

        const reliefTitle = document.createElement('div');
        reliefTitle.className = 'gw-basemap-mode-title';
        reliefTitle.innerText = 'TERRAIN SHADING';
        reliefContainer.appendChild(reliefTitle);

        const reliefToggleGroup = document.createElement('div');
        reliefToggleGroup.className = 'gw-basemap-mode-toggle';

        const isHypsometric = state.hypsometricTintEnabled !== false;

        const colorBtn = document.createElement('button');
        colorBtn.className = `gw-basemap-mode-btn ${isHypsometric ? 'active' : ''}`;
        colorBtn.innerHTML = `<span>🎨</span><span>Color Tint</span>`;
        colorBtn.title = 'Shelton/Raven Hypsometric Elevation Colors';
        colorBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          store.update({ hypsometricTintEnabled: true, savedBasemapOverrideState: null });
        });

        const monoBtn = document.createElement('button');
        monoBtn.className = `gw-basemap-mode-btn ${!isHypsometric ? 'active' : ''}`;
        monoBtn.innerHTML = `<span>⛰️</span><span>Monochrome</span>`;
        monoBtn.title = 'Neutral Natural Hillshading';
        monoBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          store.update({ hypsometricTintEnabled: false, savedBasemapOverrideState: null });
        });

        reliefToggleGroup.appendChild(colorBtn);
        reliefToggleGroup.appendChild(monoBtn);
        reliefContainer.appendChild(reliefToggleGroup);
        this.controlsContainer.appendChild(reliefContainer);
      }
    } else if (theme === 'usgs-topo' || theme === 'usgs-naip' || theme === 'usgs-hillshade') {
      // USGS Raster Layer Notice
      const badge = document.createElement('div');
      badge.className = 'gw-basemap-info-badge';
      badge.innerHTML = `<span>🌐</span><span>USGS Official Map Service (Fixed Raster)</span>`;
      this.controlsContainer.appendChild(badge);
    }

    // 3. Map Overlays (Universal across all basemaps: Street, Terrain, USGS Topo, USGS Satellite, etc.)
    const overlaysContainer = document.createElement('div');
    overlaysContainer.className = 'gw-basemap-mode-container';
    overlaysContainer.style.marginTop = '8px';

    const overlaysTitle = document.createElement('div');
    overlaysTitle.className = 'gw-basemap-mode-title';
    overlaysTitle.innerText = 'MAP OVERLAYS & 3D';
    overlaysContainer.appendChild(overlaysTitle);

    const overlaysToggleGroup = document.createElement('div');
    overlaysToggleGroup.className = 'gw-basemap-mode-toggle';
    overlaysToggleGroup.style.display = 'grid';
    overlaysToggleGroup.style.gridTemplateColumns = 'repeat(2, 1fr)';
    overlaysToggleGroup.style.gap = '6px';

    const hasTrailsAccess = TierAccessService.userHasLayerAccess('basemap_hiking_trails', state);
    const hasPublicLandsAccess = TierAccessService.userHasLayerAccess('basemap_public_lands', state);
    const hasDemAccess = TierAccessService.userHasLayerAccess('basemap_elevation_dem', state);
    const hasContoursAccess = TierAccessService.userHasLayerAccess('basemap_elevation_contours', state);

    // 3a. Hiking Trails Button
    const isTrails = state.hikingTrailsOverlayEnabled === true && hasTrailsAccess;
    const trailsBtn = document.createElement('button');
    trailsBtn.className = `gw-basemap-mode-btn ${isTrails ? 'active' : ''}`;
    trailsBtn.innerHTML = `<span>🥾</span><span>Trails</span>${!hasTrailsAccess ? this.renderTierPillHtml(getRequiredTierForLayer('basemap_hiking_trails')) : ''}`;
    trailsBtn.title = 'Toggle North America Hiking Trails Overlay (Zoom 0-14)';
    trailsBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!TierAccessService.userHasLayerAccess('basemap_hiking_trails', store.getState())) {
        TierAccessService.openTierUpgradeAction(getRequiredTierForLayer('basemap_hiking_trails'));
        return;
      }
      store.update({ hikingTrailsOverlayEnabled: !store.getState().hikingTrailsOverlayEnabled });
    });

    // 3b. Public Lands Management Button
    const isPublicLands = state.publicLandsOverlayEnabled === true && hasPublicLandsAccess;
    const publicLandsBtn = document.createElement('button');
    publicLandsBtn.className = `gw-basemap-mode-btn ${isPublicLands ? 'active' : ''}`;
    publicLandsBtn.innerHTML = `<span>🌲</span><span>Public Lands</span>${!hasPublicLandsAccess ? this.renderTierPillHtml(getRequiredTierForLayer('basemap_public_lands')) : ''}`;
    publicLandsBtn.title = 'Toggle North America Public Land Management Boundaries (BLM, USFS, NPS, State, Canadian Crown Land)';
    publicLandsBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!TierAccessService.userHasLayerAccess('basemap_public_lands', store.getState())) {
        TierAccessService.openTierUpgradeAction(getRequiredTierForLayer('basemap_public_lands'));
        return;
      }
      store.update({ publicLandsOverlayEnabled: !store.getState().publicLandsOverlayEnabled });
    });

    // 3c. 3D Terrain Pitch Button (Geometry only)
    const is3d = state.terrain3dEnabled === true && hasDemAccess;
    const threeDBtn = document.createElement('button');
    threeDBtn.className = `gw-basemap-mode-btn ${is3d ? 'active' : ''}`;
    threeDBtn.innerHTML = `<span>🏔️</span><span>3D Terrain</span>${!hasDemAccess ? this.renderTierPillHtml(getRequiredTierForLayer('basemap_elevation_dem')) : ''}`;
    threeDBtn.title = 'Toggle 3D Elevation Mesh Surface Pitch';
    threeDBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!TierAccessService.userHasLayerAccess('basemap_elevation_dem', store.getState())) {
        TierAccessService.openTierUpgradeAction(getRequiredTierForLayer('basemap_elevation_dem'));
        return;
      }
      const next3d = !store.getState().terrain3dEnabled;
      store.update({
        terrain3dEnabled: next3d,
        panTiltZoomEnabled: next3d,
        savedBasemapOverrideState: null
      });
    });

    // 3c. Pan / Tilt / Zoom Controls Toggle Button
    const isPtz = state.panTiltZoomEnabled === true;
    const ptzBtn = document.createElement('button');
    ptzBtn.className = `gw-basemap-mode-btn ${isPtz ? 'active' : ''}`;
    ptzBtn.innerHTML = `<span>🎮</span><span>Pan/Tilt/Zoom</span>`;
    ptzBtn.title = 'Toggle On-Screen 3D Pan, Tilt, and Zoom Controls';
    ptzBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      store.update({ panTiltZoomEnabled: !store.getState().panTiltZoomEnabled });
    });

    // 3d. Relief Shading Button (Independent Hillshading Lighting)
    const isShading = Boolean(state.reliefShadingEnabled) && hasDemAccess;
    const shadingBtn = document.createElement('button');
    shadingBtn.className = `gw-basemap-mode-btn ${isShading ? 'active' : ''}`;
    shadingBtn.innerHTML = `<span>⛰️</span><span>Relief Shading</span>${!hasDemAccess ? this.renderTierPillHtml(getRequiredTierForLayer('basemap_elevation_dem')) : ''}`;
    shadingBtn.title = 'Toggle Natural Topographic Hillshading & Relief Shadows';
    shadingBtn.style.gridColumn = 'span 2';
    shadingBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!TierAccessService.userHasLayerAccess('basemap_elevation_dem', store.getState())) {
        TierAccessService.openTierUpgradeAction(getRequiredTierForLayer('basemap_elevation_dem'));
        return;
      }
      store.update({ reliefShadingEnabled: !store.getState().reliefShadingEnabled });
    });

    // 3e. Elevation Contours Button (Not needed on USGS Topo which already has native contours)
    const effectiveTheme = TierAccessService.userHasBasemapAccess(theme, 'off', state) ? theme : 'osm';
    const isTopoTheme = effectiveTheme === 'usgs-topo';
    const isContours = state.elevationContoursEnabled === true && !isTopoTheme && hasContoursAccess;
    const contoursBtn = document.createElement('button');
    contoursBtn.className = `gw-basemap-mode-btn ${isContours ? 'active' : ''} ${isTopoTheme ? 'disabled' : ''}`;
    contoursBtn.innerHTML = `<span>📈</span><span>Contours</span>${(!isTopoTheme && !hasContoursAccess) ? this.renderTierPillHtml(getRequiredTierForLayer('basemap_elevation_contours')) : ''}`;
    contoursBtn.title = isTopoTheme
      ? 'USGS Topo basemap already features native topographic contour lines'
      : 'Toggle Dynamic Vector Elevation Contours (Major & Minor lines with labels)';
    contoursBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (isTopoTheme) return;
      if (!TierAccessService.userHasLayerAccess('basemap_elevation_contours', store.getState())) {
        TierAccessService.openTierUpgradeAction(getRequiredTierForLayer('basemap_elevation_contours'));
        return;
      }
      store.update({ elevationContoursEnabled: !store.getState().elevationContoursEnabled });
    });

    overlaysToggleGroup.appendChild(threeDBtn);
    overlaysToggleGroup.appendChild(ptzBtn);
    overlaysToggleGroup.appendChild(trailsBtn);
    overlaysToggleGroup.appendChild(publicLandsBtn);
    overlaysToggleGroup.appendChild(contoursBtn);
    overlaysToggleGroup.appendChild(shadingBtn);
    overlaysContainer.appendChild(overlaysToggleGroup);
    this.controlsContainer.appendChild(overlaysContainer);

    // 4. Shading Intensity Slider (Active whenever Relief Shading is enabled)
    if (Boolean(state.reliefShadingEnabled)) {
      const shadingContainer = document.createElement('div');
      shadingContainer.className = 'gw-basemap-mode-container';
      shadingContainer.style.marginTop = '8px';

      const headerRow = document.createElement('div');
      headerRow.style.display = 'flex';
      headerRow.style.justifyContent = 'space-between';
      headerRow.style.alignItems = 'center';
      headerRow.style.marginBottom = '6px';

      const shadingTitle = document.createElement('div');
      shadingTitle.className = 'gw-basemap-mode-title';
      shadingTitle.innerText = 'SHADING INTENSITY';
      headerRow.appendChild(shadingTitle);

      const valBadge = document.createElement('span');
      valBadge.className = 'gw-range-val';
      valBadge.style.fontSize = '0.72rem';
      valBadge.style.fontFamily = 'monospace';
      valBadge.style.color = '#38bdf8';
      valBadge.style.fontWeight = 'bold';
      const currentIntensity = state.reliefShadingIntensity !== undefined ? state.reliefShadingIntensity : 0.40;
      valBadge.innerText = `${Math.round(currentIntensity * 100)}%`;
      headerRow.appendChild(valBadge);

      shadingContainer.appendChild(headerRow);

      const sliderRow = document.createElement('div');
      sliderRow.style.display = 'flex';
      sliderRow.style.alignItems = 'center';
      sliderRow.style.gap = '8px';

      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = '0';
      slider.max = '100';
      slider.step = '5';
      slider.value = String(Math.round(currentIntensity * 100));
      slider.style.flex = '1';
      slider.style.accentColor = '#38bdf8';
      slider.style.cursor = 'pointer';

      slider.addEventListener('input', (e) => {
        e.stopPropagation();
        const newVal = parseInt(slider.value, 10) / 100.0;
        valBadge.innerText = `${Math.round(newVal * 100)}%`;
        store.update({ reliefShadingIntensity: newVal, reliefShadingEnabled: true });
      });

      sliderRow.appendChild(slider);
      shadingContainer.appendChild(sliderRow);
      this.controlsContainer.appendChild(shadingContainer);
    }

    // 5. Terrain Exaggeration Slider (Active whenever 3D Relief is ON or Vector theme is active)
    const isTerrainTheme = this.isHostedVectorTheme(theme);
    if (state.terrain3dEnabled || isTerrainTheme) {
      const exagContainer = document.createElement('div');
      exagContainer.className = 'gw-basemap-mode-container';
      exagContainer.style.marginTop = '8px';

      const headerRow = document.createElement('div');
      headerRow.style.display = 'flex';
      headerRow.style.justifyContent = 'space-between';
      headerRow.style.alignItems = 'center';
      headerRow.style.marginBottom = '6px';

      const exagTitle = document.createElement('div');
      exagTitle.className = 'gw-basemap-mode-title';
      exagTitle.innerText = 'TERRAIN EXAGGERATION';
      headerRow.appendChild(exagTitle);

      const valBadge = document.createElement('span');
      valBadge.className = 'gw-range-val';
      valBadge.style.fontSize = '0.72rem';
      valBadge.style.fontFamily = 'monospace';
      valBadge.style.color = '#38bdf8';
      valBadge.style.fontWeight = 'bold';
      const currentExag = state.terrainExaggeration || 1.0;
      valBadge.innerText = `${currentExag.toFixed(1)}x`;
      headerRow.appendChild(valBadge);

      exagContainer.appendChild(headerRow);

      const sliderRow = document.createElement('div');
      sliderRow.style.display = 'flex';
      sliderRow.style.alignItems = 'center';
      sliderRow.style.gap = '8px';

      const slider = document.createElement('input');
      slider.type = 'range';
      slider.min = '10';
      slider.max = '25';
      slider.step = '1';
      slider.value = String(Math.round(currentExag * 10));
      slider.style.flex = '1';
      slider.style.accentColor = '#38bdf8';
      slider.style.cursor = 'pointer';

      slider.addEventListener('input', (e) => {
        e.stopPropagation();
        const newVal = parseInt(slider.value, 10) / 10.0;
        valBadge.innerText = `${newVal.toFixed(1)}x`;
        store.update({ terrainExaggeration: newVal, terrain3dEnabled: true });
      });

      sliderRow.appendChild(slider);
      exagContainer.appendChild(sliderRow);
      this.controlsContainer.appendChild(exagContainer);
    }
  }

  private setupStoreSubscription() {
    let lastTheme = store.getState().theme;
    let lastMode = store.getState().vectorThemeMode;
    let lastHypso = store.getState().hypsometricTintEnabled;
    let lastTrails = store.getState().hikingTrailsOverlayEnabled;
    let lastPublicLands = store.getState().publicLandsOverlayEnabled;
    let last3d = store.getState().terrain3dEnabled;
    let lastPtz = store.getState().panTiltZoomEnabled;
    let lastExag = store.getState().terrainExaggeration;
    let lastShading = store.getState().reliefShadingEnabled;
    let lastShadingIntensity = store.getState().reliefShadingIntensity;
    let lastContours = store.getState().elevationContoursEnabled;
    let lastRecentSatProduct = store.getState().recentSatelliteProduct;
    let lastRecentSatOffset = store.getState().recentSatelliteDayOffset;
    let lastRecentSatOpacity = store.getState().recentSatelliteOpacity;
    let lastRecentSatMountainMode = store.getState().recentSatelliteMountainMode;
    let lastRecentSatHlsSelected = (store.getState().recentSatelliteHlsSelectedDates || []).join(',');
    let lastRecentSatDateOverlay = store.getState().recentSatelliteDateOverlay;
    let lastUserTier = TierAccessService.getUserAccessTier(store.getState());

    store.subscribe((state) => {
      const currentUserTier = TierAccessService.getUserAccessTier(state);
      const userTierChanged = currentUserTier !== lastUserTier;
      const themeChanged = state.theme !== lastTheme;
      const modeChanged = state.vectorThemeMode !== lastMode;
      const hypsoChanged = state.hypsometricTintEnabled !== lastHypso;
      const trailsChanged = state.hikingTrailsOverlayEnabled !== lastTrails;
      const publicLandsChanged = state.publicLandsOverlayEnabled !== lastPublicLands;
      const threeDChanged = state.terrain3dEnabled !== last3d;
      const ptzChanged = state.panTiltZoomEnabled !== lastPtz;
      const exagChanged = state.terrainExaggeration !== lastExag;
      const shadingChanged = state.reliefShadingEnabled !== lastShading;
      const shadingIntensityChanged = state.reliefShadingIntensity !== lastShadingIntensity;
      const contoursChanged = state.elevationContoursEnabled !== lastContours;
      const recentSatProductChanged = state.recentSatelliteProduct !== lastRecentSatProduct;
      const recentSatOffsetChanged = state.recentSatelliteDayOffset !== lastRecentSatOffset;
      const recentSatOpacityChanged = state.recentSatelliteOpacity !== lastRecentSatOpacity;
      const recentSatMountainModeChanged = state.recentSatelliteMountainMode !== lastRecentSatMountainMode;
      const recentSatHlsSelectedChanged = (state.recentSatelliteHlsSelectedDates || []).join(',') !== lastRecentSatHlsSelected;
      const recentSatDateOverlayChanged = state.recentSatelliteDateOverlay !== lastRecentSatDateOverlay;

        if (userTierChanged || themeChanged || modeChanged || hypsoChanged || trailsChanged || publicLandsChanged || threeDChanged || ptzChanged || exagChanged || shadingChanged || shadingIntensityChanged || contoursChanged || recentSatProductChanged || recentSatOffsetChanged || recentSatOpacityChanged || recentSatMountainModeChanged || recentSatHlsSelectedChanged || recentSatDateOverlayChanged) {
          lastUserTier = currentUserTier;
          lastTheme = state.theme;
          lastMode = state.vectorThemeMode;
          lastHypso = state.hypsometricTintEnabled;
          lastTrails = state.hikingTrailsOverlayEnabled;
          lastPublicLands = state.publicLandsOverlayEnabled;
          last3d = state.terrain3dEnabled;
          lastPtz = state.panTiltZoomEnabled;
          lastExag = state.terrainExaggeration;
          lastShading = state.reliefShadingEnabled;
          lastShadingIntensity = state.reliefShadingIntensity;
          lastContours = state.elevationContoursEnabled;
          lastRecentSatProduct = state.recentSatelliteProduct;
          lastRecentSatOffset = state.recentSatelliteDayOffset;
          lastRecentSatOpacity = state.recentSatelliteOpacity;
          lastRecentSatMountainMode = state.recentSatelliteMountainMode;
          lastRecentSatHlsSelected = (state.recentSatelliteHlsSelectedDates || []).join(',');
          lastRecentSatDateOverlay = state.recentSatelliteDateOverlay;

          this.updateThumbnailLockBadges(state);
          this.updateCardSelection(state.theme);
          this.renderSubControls(state);
        }
      });
  }


  private updateCardSelection(key: string) {
    const state = store.getState();
    const effectiveKey = TierAccessService.userHasBasemapAccess(key, 'off', state) ? key : 'osm';
    const cards = this.popoverCard.querySelectorAll('.gw-basemap-thumbnail-card');
    cards.forEach(c => {
      const cardKey = (c as HTMLElement).dataset.key;
      const isSelected = cardKey === effectiveKey ||
        (cardKey === 'osm' && (effectiveKey === 'osm' || effectiveKey === 'openstreetmap')) ||
        (cardKey === 'road' && (effectiveKey === 'light' || effectiveKey === 'dark' || effectiveKey === 'terrain-mapterhorn' || effectiveKey === 'terrain' || effectiveKey === 'neutral' || !effectiveKey));
      if (isSelected) {
        c.classList.add('gw-selected');
      } else {
        c.classList.remove('gw-selected');
      }
    });
  }

  public selectBasemap(key: string) {
    if (!TierAccessService.userHasBasemapAccess(key, 'off', store.getState())) {
      TierAccessService.openTierUpgradeAction(getRequiredTierForBasemapTheme(key, 'off'));
      return;
    }
    this.updateCardSelection(key);
    this.onBasemapSelect(key);
  }

  public togglePopover() {
    if (this.isOpen) {
      this.closePopover();
    } else {
      this.openPopover();
    }
  }

  public openPopover() {
    this.isOpen = true;
    this.popoverCard.classList.remove('gw-hidden');
    this.renderSubControls(store.getState());
  }

  public closePopover() {
    this.isOpen = false;
    this.popoverCard.classList.add('gw-hidden');
  }
}
